/**
 * HTTP Transport for Tradovate REST API
 *
 * Implements Transport interface with:
 * - Bearer token authentication
 * - P-ticket retry handling
 * - Error handling
 * - Timeout support
 *
 * Ported from: http.rs (378 lines)
 */

import type { Transport } from './types';
import { TransportError } from './types';

/**
 * HTTP Transport implementation using fetch API
 *
 * Rust equivalent: HttpTransport (tokio + reqwest / wasm + web-sys)
 */
export class HttpTransport implements Transport {
  private readonly timeout: number;

  /**
   * @param baseUrl - Base URL (e.g., "https://demo.tradovateapi.com/v1")
   * @param getAuthToken - Function that returns current auth token (null if not authenticated)
   * @param timeoutMs - Request timeout in milliseconds (default 30s)
   */
  constructor(
    private baseUrl: string,
    private getAuthToken: () => string | null,
    timeoutMs: number = 30000
  ) {
    // Ensure URL ends with /
    if (!this.baseUrl.endsWith('/')) {
      this.baseUrl += '/';
    }
    this.timeout = timeoutMs;
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string): string {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseUrl}${cleanEndpoint}`;
  }

  /**
   * Execute HTTP request with timeout
   *
   * Rust equivalent: execute_request(method, endpoint, body)
   */
  private async executeRequest(
    method: string,
    endpoint: string,
    body?: any
  ): Promise<any> {
    const url = this.buildUrl(endpoint);

    console.log(`🌐 HTTP ${method} ${url}`);

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    // Add auth token if available
    const authToken = this.getAuthToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
      // Avoid logging sensitive token data
      console.debug('🔐 Auth token present (redacted)');
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Make request
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const status = response.status;
      console.log(`📥 HTTP Response: ${status} ${response.statusText}`);

      // Read response text
      const responseText = await response.text();

      // Handle success responses
      if (response.ok) {
        // Parse JSON
        let responseData;
        try {
          responseData = responseText ? JSON.parse(responseText) : null;
        } catch (error) {
          console.error('Failed to parse JSON response:', error);
          throw new TransportError(
            `Failed to parse JSON response: ${error}`,
            'JSON_PARSE_ERROR',
            status
          );
        }

        return responseData;
      }

      // Handle error responses
      let parsedErrorData: any = undefined;
      try {
        parsedErrorData = responseText ? JSON.parse(responseText) : undefined;
      } catch (_) {
        // Attach raw text if not JSON
        parsedErrorData = { text: responseText };
      }

      throw new TransportError(
        `HTTP ${status}: ${response.statusText}`,
        'HTTP_ERROR',
        status,
        parsedErrorData
      );

    } catch (error: unknown) {
      clearTimeout(timeoutId);

      if (error instanceof TransportError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new TransportError(
          `Request timed out after ${this.timeout}ms`,
          'TIMEOUT'
        );
      }

      throw new TransportError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'NETWORK_ERROR'
      );
    }
  }

  /**
   * GET request
   *
   * @param path - API endpoint
   * @param params - Query parameters (optional)
   */
  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    let endpoint = path;

    // Add query parameters if provided
    if (params && Object.keys(params).length > 0) {
      const queryString = new URLSearchParams(
        Object.entries(params).map(([k, v]) => [k, String(v)])
      ).toString();
      endpoint = `${path}?${queryString}`;
    }

    return this.executeRequest('GET', endpoint);
  }

  /**
   * POST request
   *
   * @param path - API endpoint
   * @param body - Request body (optional)
   */
  async post<T = any>(path: string, body?: any): Promise<T> {
    return this.executeRequest('POST', path, body);
  }

  /**
   * PUT request
   */
  async put<T = any>(path: string, body?: any): Promise<T> {
    return this.executeRequest('PUT', path, body);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(path: string): Promise<T> {
    return this.executeRequest('DELETE', path);
  }
}
